import React, { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import SEO from "../components/SEO";
import BlogCard from "../components/BlogCard";
import NotFound from "./NotFound";
import useReveal from "../hooks/useReveal";
import { getPost, localize, getRelated } from "../data/blog";
import { SITE } from "../config/site";

const LOCALE_MAP = { tr: "tr-TR", en: "en-US", ru: "ru-RU" };

function Block({ b }) {
  switch (b.type) {
    case "h2": return <h2>{b.text}</h2>;
    case "h3": return <h3>{b.text}</h3>;
    case "quote": return <blockquote>{b.text}</blockquote>;
    case "ul": return <ul>{b.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case "ol": return <ol>{b.items.map((it, i) => <li key={i}>{it}</li>)}</ol>;
    case "p":
    default: return <p>{b.text}</p>;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "tr";
  const pageRef = useRef(null);
  const raw = getPost(slug);
  useReveal(pageRef, [slug, lang]);

  if (!raw) return <NotFound />;

  const post = localize(raw, lang);
  const related = getRelated(slug, 3);
  const dateStr = new Date(raw.date).toLocaleDateString(LOCALE_MAP[lang] || "tr-TR", { year: "numeric", month: "long", day: "numeric" });

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: raw.date,
    inLanguage: lang,
    author: { "@type": "Person", name: raw.author || SITE.name },
    publisher: { "@type": "Organization", name: SITE.name, logo: { "@type": "ImageObject", url: SITE.url + SITE.logo } },
    mainEntityOfPage: `${SITE.url}/blog/${raw.slug}`,
    keywords: (post.tags || []).join(", "),
  };

  return (
    <div ref={pageRef}>
      <SEO title={`${post.title} — ${SITE.shortName}`} description={post.excerpt} type="article" jsonLd={articleLd} />

      <section className="article-hero">
        <div className="container">
          <Link to="/blog" className="article-back link-arrow"><FiArrowLeft /> {t("blog.backToBlog")}</Link>
          <div><span className="article-cat">{t(`blog.categories.${raw.category}`)}</span></div>
          <h1 className="article-title">{post.title}</h1>
          <div className="article-meta">
            <span>{raw.author}</span><span className="dot" />
            <span>{dateStr}</span><span className="dot" />
            <span>{raw.readingMinutes} {t("blog.minRead")}</span>
          </div>
        </div>
      </section>

      <section style={{ paddingBottom: "clamp(48px,7vw,90px)" }}>
        <div className="container">
          <div className="article-cover">
            {raw.cover ? <img src={raw.cover} alt={post.title} /> : <span className="pc-grid" />}
          </div>

          <div className="article-layout">
            <article className="prose">
              {(post.body || []).map((b, i) => <Block b={b} key={i} />)}
              {post.tags && post.tags.length > 0 && (
                <div className="article-tags">
                  {post.tags.map((tag, i) => <span className="pill" key={i}>{tag}</span>)}
                </div>
              )}
            </article>
          </div>

          <div className="article-cta">
            <div>
              <h3>{t("blog.cta.title")}</h3>
              <p>{t("blog.cta.desc")}</p>
            </div>
            <Link to="/iletisim" className="btn">{t("blog.cta.btn")} <FiArrowRight className="arrow" /></Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section soft" style={{ paddingTop: "clamp(40px,5vw,64px)" }}>
          <div className="container">
            <h2 className="h-md" style={{ marginBottom: 8 }}>{t("blog.related")}</h2>
            <div className="related-grid">
              {related.map((p) => <BlogCard post={p} key={p.slug} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
